import React from "react";
import { store } from "reducers/index";
import CATEGORIES from "utils/categories";
import { ACTION_TYPE } from "reducers/actionType";

export const MOST_RELEVANT_SUBJECTS = 8;
export const LOAD_MORE_AMOUNT = 4;

/******************** Process Store  ******************/

const makeStoreObject = () => {
  const storeValues = Object.values(store.getState());
  const storeOb = storeValues.reduce((obj, item) => {
    const itemKeys = Object.keys(item);
    if (itemKeys.length === 1) {
      obj[Object.keys(item)[0]] = Object.values(item)[0];
    } else {
      if ("input" in item) {
        obj.input = item.input;
      } else if ("eligibility" in item) {
        obj.eligibility = item.eligibility;
      } else if ("recommendations" in item) {
        obj.recommendations = item.recommendations;
      } else if ("recommendations_input_hash" in item) {
        obj.recommendations_input_hash = item.recommendations_input_hash;
        obj.eligibility_input_hash = item.eligibility_input_hash;
      }
    }
    return obj;
  }, {});
  return storeOb;
};

const makeCategoriesObject = () => {
  return CATEGORIES.reduce((obj, item) => {
    obj[item.name] = item;
    return obj;
  }, {});
};

export const getCategory = (name) => {
  const categoriesObject = makeCategoriesObject();
  return categoriesObject[name];
};

/******************** Attribute Processing   ******************/

export const removeOrphanFos = (
  userFos,
  userAttributes,
  ATTRIBUTE,
  action,
  attributeFos
) => {
  return (dispatch) => {
    const newUserFos = [];
    userFos.forEach((uf) => {
      userAttributes.forEach((ua) => {
        if (ATTRIBUTE[ua].fos_code.includes(uf)) {
          newUserFos.push(uf);
        }
      });
    });
    const newDataObj = {};
    newDataObj[attributeFos] = Array.from(new Set(newUserFos));
    dispatch({
      type: action,
      data: newDataObj,
    });
  };
};

export const getAttributeByName = (ATTRIBUTE, name) => {
  const attribute = Object.values(ATTRIBUTE).find((attri) => {
    return attri.name === name;
  });
  return attribute.attribute_id.toString();
};
/******************** Summary and Eligibility ******************/

export const makeSubjectErrorOb = (eligibility) => {
  const subjectErrorOb = {};
  eligibility.details.forEach((det) => {
    if (det.status === "INELIGIBLE" || det.status === "INDETERMINATE") {
      const { messages } = det;
      messages.forEach((msg) => {
        if (msg.cause !== null) {
          subjectErrorOb[msg.cause.toString()] = msg;
        }
      });
    }
  });
  return subjectErrorOb;
};

export const makeFailureMessages = (eligibility) => {
  const failureMessages = [];
  eligibility.details.forEach((det) => {
    if (det.status === "INELIGIBLE" || det.status === "INDETERMINATE") {
      const { messages } = det;
      messages.forEach((msg) => {
        if (msg.cause === null) {
          failureMessages.push(msg.message);
        }
      });
    }
  });
  return failureMessages;
};

export const isPopulated = (category) => {
  const storeObject = makeStoreObject();
  for (let i = 0; i < category.inputSource.length; i++) {
    const currInputSource = storeObject[category.inputSource[i]];
    if (currInputSource.length > 0) {
      return true;
    }
  }
  return false;
};

export const makeAttributeInput = (userAttribute, userFos, ATTRIBUTE) => {
  const userInput = [];
  userAttribute.forEach((id) => {
    const attributeFos = { id: id, fos_codes: [] };
    ATTRIBUTE[id].fos_code.forEach((fos) => {
      if (userFos.includes(fos)) {
        attributeFos.fos_codes.push(fos);
      }
    });
    userInput.push(attributeFos);
  });
  return userInput;
};

const makeInstitutionsInput = (userCourses) => {
  // also construct the institutions input
  const { courses } = makeStoreObject().input;
  const userInstitutions = [];
  userCourses.forEach((key) => {
    const course = courses[key];
    const abbr = course.institution;
    const addedInst = userInstitutions.find((inst) => abbr === inst.id);
    if (addedInst) {
      addedInst.course_codes.push(key);
    } else {
      userInstitutions.push({ id: abbr, course_codes: [key] });
    }
  });
  return userInstitutions;
};

export const collectUserInput = () => {
  const storeObject = makeStoreObject();
  // Make attribute input
  const { input } = storeObject;
  if (!input) {
    console.error("input not found");
    return;
  }
  const { courses } = input;
  const { INTEREST, SKILL, CAREER } = input.attributes;
  const {
    userInterests,
    userSkills,
    userCareers,
    userPersonality,
    userCourses,
    userStudys,
  } = storeObject;

  const userInput = {
    skills: makeAttributeInput(userSkills, userStudys, SKILL),
    careers: makeAttributeInput(userCareers, userStudys, CAREER),
    interests: makeAttributeInput(userInterests, userStudys, INTEREST),
    courses: {
      institutions: makeInstitutionsInput(userCourses),
      fos_codes: userCourses.map((course) => courses[course].primary_fos),
    },
    personality_attributes: userPersonality,
  };

  return userInput;
};

export const hydrateUserSubjects = (selectedSubjects) => {
  return (dispatch) => {
    const storeObject = makeStoreObject();
    const { recommendedSubjects, otherSubjects } = storeObject;

    const userRecSubs = [];
    const userAddSubs = [];
    const userOthSubs = [];

    selectedSubjects.forEach((subject) => {
      if (recommendedSubjects.includes(subject)) {
        userRecSubs.push(subject);
      } else if (otherSubjects.includes(subject)) {
        userOthSubs.push(subject);
      } else {
        userAddSubs.push(subject);
      }
    });
    dispatch({
      type: ACTION_TYPE.pickRecSubs,
      data: { userRecSubs },
    });
    dispatch({
      type: ACTION_TYPE.pickOthSubs,
      data: { userOthSubs },
    });
    dispatch({
      type: ACTION_TYPE.pickAddSubs,
      data: { userAddSubs },
    });
  };
};

/******************** Common Rendering Functions   ******************/
export const renderProgressIcon = (
  displayName,
  active,
  complete,
  iconSource,
  iconDimensions
) => {
  if (complete) {
    return (
      <img
        src={require(`assets/${iconSource.complete}`)}
        alt={`${displayName} icon complete`}
        width={iconDimensions.complete.width}
        height={iconDimensions.complete.height}
        className="icon"
      />
    );
  } else if (active) {
    return (
      <img
        src={require(`assets/${iconSource.active}`)}
        alt={`${displayName} icon `}
        width={iconDimensions.normal.width}
        height={iconDimensions.normal.height}
        className="icon"
      />
    );
  } else {
    return (
      <img
        src={require(`assets/${iconSource.normal}`)}
        alt={`${displayName} icon `}
        width={iconDimensions.normal.width}
        height={iconDimensions.normal.height}
        className="icon"
      />
    );
  }
};

export const renderFailureMessages = (failureMessages) => {
  return (
    <ul className="eligibility-dotpoints ">
      {failureMessages.map((message) => (
        <li className="margin-bottom" key={message}>
          {message}
        </li>
      ))}
    </ul>
  );
};

export const atLeastOneContributor = () => {
  for (let i = 0; i < CATEGORIES.length; i++) {
    if (isPopulated(CATEGORIES[i]) && CATEGORIES[i].contributor) {
      return true;
    }
  }
  return false;
};

export const getSubjects = () => {
  const storeObject = makeStoreObject();
  const { subjects } = storeObject.input;
  return subjects;
};

export const selectFilter = (inputValue, option) =>
  option.props.children.toLowerCase().indexOf(inputValue.toLowerCase().trim()) >
  -1;

export const makeObjectWithIdAsKey = (origArray) => {
  // takes in array of objects each containing an id
  // returns object with keys of ids
  const finalObject = origArray.reduce((finalOb, newOb) => {
    finalOb[newOb.id] = newOb;
    return finalOb;
  }, {});
  return finalObject;
};

export const makeObjectWithNameAsKey = (origArray, name) => {
  // takes in array of objects each containing an id
  // returns object with keys of ids
  const finalObject = origArray.reduce((finalOb, newOb) => {
    finalOb[newOb[name]] = newOb;
    return finalOb;
  }, {});
  return finalObject;
};

export const searchAttributes = (search, attribute) =>
  attribute.toLowerCase().indexOf(search.toLowerCase().trim()) === 0;
